"use client";

import { useMemo } from "react";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";

import { useProtectedQuery } from "@/hooks/use-protected-query";
import { fetchData } from "@/utils/api-handler";
import type { CommandAgent } from "@/types/command-agent";
import {
    getExpiredNamesFromAgentInline,
    resolveExpiredIntegrationNames,
    resolveStatusRows,
    type IntegrationLookupRow,
} from "@/utils/command-agent-expired-integrations";

interface UseCommandAgentExpiredIntegrationsOptions {
    /** When set, skip fetching and derive from this agent payload only. */
    agent?: CommandAgent | null;
    enabled?: boolean;
}

/**
 * Returns expired third-party app names for a Command agent.
 * Add `?test_expired_integrations=1` to preview using ACTIVE integrations.
 */
export function useCommandAgentExpiredIntegrations(
    agentId: string | null | undefined,
    options?: UseCommandAgentExpiredIntegrationsOptions
) {
    const searchParams = useSearchParams();
    const testPreviewWithActive = searchParams.get("test_expired_integrations") === "1";
    const enabled = options?.enabled ?? Boolean(agentId);
    const inlineAgent = options?.agent;

    const { data: fetchedAgent, isLoading: isLoadingAgent } = useProtectedQuery<CommandAgent>({
        queryKey: ["command-agent-header", agentId],
        queryFn: () => fetchData(`/api/command-team/agents/${agentId}`),
        enabled: enabled && Boolean(agentId) && !inlineAgent,
        requiredPermissions: ["VIEW_COMMAND_AGENT"],
        staleTime: 0,
    });

    const agent = inlineAgent ?? fetchedAgent;

    const { data: integrationLookupData, isLoading: isLoadingIntegrationLookup } = useQuery<{
        items: IntegrationLookupRow[];
    }>({
        queryKey: ["integrations-apps-lookup"],
        queryFn: async () => {
            const params = new URLSearchParams({ page: "1", limit: "50" });
            return fetchData(`/api/integrations/apps?${params.toString()}`);
        },
        enabled: enabled && Boolean(agentId) && Boolean(agent),
        staleTime: 60_000,
    });

    const statusRows = useMemo(
        () => resolveStatusRows(agent, integrationLookupData?.items ?? []),
        [agent, integrationLookupData?.items]
    );

    const { expiredNames, isTestPreview } = useMemo(
        () =>
            resolveExpiredIntegrationNames(statusRows, {
                testPreviewWithActive,
            }),
        [statusRows, testPreviewWithActive]
    );

    const inlineExpiredNames = useMemo(() => getExpiredNamesFromAgentInline(agent), [agent]);

    const resolvedNames = expiredNames.length > 0 ? expiredNames : inlineExpiredNames;

    const isLoading =
        enabled &&
        ((Boolean(agentId) && !inlineAgent && isLoadingAgent) ||
            (Boolean(agentId) && Boolean(agent) && isLoadingIntegrationLookup));

    return {
        expiredNames: resolvedNames,
        hasExpired: resolvedNames.length > 0,
        isLoading,
        isTestPreview,
    };
}
